// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
import { IRouter, ILabShell, } from '@jupyterlab/application';
import { Clipboard, ICommandPalette, Dialog, showDialog } from '@jupyterlab/apputils';
import { PageConfig, URLExt } from '@jupyterlab/coreutils';
import { IDocumentManager } from '@jupyterlab/docmanager';
import { IDocumentProviderFactory, ProviderMock, } from '@jupyterlab/docprovider';
import { IFileBrowserFactory } from '@jupyterlab/filebrowser';
import { IMainMenu } from '@jupyterlab/mainmenu';
import { ITranslator } from '@jupyterlab/translation';
import { downloadIcon, linkIcon } from '@jupyterlab/ui-components';
import { liteIcon, liteWordmark } from '@jupyterlite/ui-components';
import { filter, toArray } from '@lumino/algorithm';
import { Widget } from '@lumino/widgets';
import { getParam } from 'lib0/environment';
import React from 'react';
/**
 * The default notebook factory.
 */
const NOTEBOOK_FACTORY = 'Notebook';
/**
 * The editor factory.
 */
const EDITOR_FACTORY = 'Editor';
/**
 * A regular expression to match path to notebooks, documents and consoles
 */
const URL_PATTERN = new RegExp('/(lab|notebooks|edit|consoles)\\/?');
/**
 * The command IDs used by the application extension.
 */
var CommandIDs;
(function (CommandIDs) {
    CommandIDs.about = 'application:about';
    CommandIDs.docmanagerDownload = 'docmanager:download';
    CommandIDs.filebrowserDownload = 'filebrowser:download';
    CommandIDs.copyShareableLink = 'filebrowser:share-main';
})(CommandIDs || (CommandIDs = {}));
/**
 * The name of the translation bundle for internationalized strings.
 */
const I18N_BUNDLE = 'jupyterlite';
/**
 * Add a command to show an About dialog.
 */
const about = {
    id: '@jupyterlite/application-extension:about',
    autoStart: true,
    requires: [ITranslator],
    optional: [ICommandPalette, IMainMenu],
    activate: (app, translator, palette, menu) => {
        const { commands } = app;
        const trans = translator.load(I18N_BUNDLE);
        const category = trans.__('Help');
        commands.addCommand(CommandIDs.about, {
            label: trans.__('About %1', app.name),
            execute: () => {
                const versionNumber = trans.__('Version %1', app.version);
                const versionInfo = (React.createElement("span", { className: "jp-About-version-info" },
                    React.createElement("span", { className: "jp-About-version" }, versionNumber)));
                const title = (React.createElement("span", { className: "jp-About-header" },
                    React.createElement("div", { className: "jp-About-header-info" },
                        React.createElement(liteWordmark.react, { height: "auto", width: "196px" }),
                        versionInfo)));
                // Create the body of the about dialog
                const jupyterliteURL = 'https://github.com/jupyterlite/jupyterlite';
                const contributorsURL = 'https://github.com/jupyterlite/jupyterlite/graphs/contributors';
                const externalLinks = (React.createElement("span", { className: "jp-About-externalLinks" },
                    React.createElement("a", { href: contributorsURL, target: "_blank", rel: "noopener noreferrer", className: "jp-Button-flat" }, trans.__('CONTRIBUTOR LIST')),
                    React.createElement("a", { href: jupyterliteURL, target: "_blank", rel: "noopener noreferrer", className: "jp-Button-flat" }, trans.__('JUPYTERLITE ON GITHUB'))));
                const copyright = (React.createElement("span", { className: "jp-About-copyright" }, trans.__('© 2021 JupyterLite Contributors')));
                const body = (React.createElement("div", { className: "jp-About-body" },
                    externalLinks,
                    copyright));
                return showDialog({
                    title,
                    body,
                    buttons: [
                        Dialog.createButton({
                            label: trans.__('Dismiss'),
                            className: 'jp-About-button jp-mod-reject jp-mod-styled',
                        }),
                    ],
                });
            },
        });
        if (palette) {
            palette.addItem({ command: CommandIDs.about, category });
        }
        if (menu) {
            menu.helpMenu.addGroup([{ command: CommandIDs.about }], 0);
        }
    },
};
/**
 * An alternative document provider plugin
 */
const docProviderPlugin = {
    id: '@jupyterlite/application-extension:docprovider',
    provides: IDocumentProviderFactory,
    requires: [ITranslator],
    activate: (app, translator) => {
        const collaborative = PageConfig.getOption('collaborative') === 'true';
        const factory = (options) => {
            if (collaborative) {
                const trans = translator.load(I18N_BUNDLE);
                console.warn(trans.__('The `collaborative` feature was enabled, but no docprovider is available.'));
                console.info(trans.__('Install `jupyterlab-webrtc-docprovider` to enable WebRTC-based collaboration.'));
            }
            return new ProviderMock();
        };
        return factory;
    },
};
/**
 * A plugin providing download commands in the file menu and command palette.
 */
const downloadPlugin = {
    id: '@jupyterlite/application-extension:download',
    autoStart: true,
    requires: [ITranslator, IDocumentManager],
    optional: [ICommandPalette, IFileBrowserFactory],
    activate: (app, translator, docManager, palette, factory) => {
        const trans = translator.load(I18N_BUNDLE);
        const { commands, serviceManager, shell } = app;
        const { contents } = serviceManager;
        const isEnabled = () => {
            const { currentWidget } = shell;
            return !!(currentWidget && docManager.contextForWidget(currentWidget));
        };
        const downloadContent = (content, path) => {
            const element = document.createElement('a');
            element.href = `data:text/json;charset=utf-8,${encodeURIComponent(content)}`;
            element.download = path;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        };
        const formatContent = async (path) => {
            const model = await contents.get(path, { content: true });
            if (model.type === 'notebook' || model.mimetype.indexOf('json') !== -1) {
                return JSON.stringify(model.content, null, 2);
            }
            return model.content;
        };
        commands.addCommand(CommandIDs.docmanagerDownload, {
            label: trans.__('Download'),
            caption: trans.__('Download the file to your computer'),
            isEnabled,
            execute: async () => {
                // Checks that shell.currentWidget is valid:
                const current = shell.currentWidget;
                if (!isEnabled() || !current) {
                    return;
                }
                const context = docManager.contextForWidget(current);
                if (!context) {
                    return showDialog({
                        title: trans.__('Cannot Download'),
                        body: trans.__('No context found for current widget!'),
                        buttons: [Dialog.okButton({ label: trans.__('OK') })],
                    });
                }
                const content = await formatContent(context.path);
                downloadContent(content, context.path);
            },
        });
        const category = trans.__('File Operations');
        if (palette) {
            palette.addItem({ command: CommandIDs.docmanagerDownload, category });
        }
        if (factory) {
            const { tracker } = factory;
            commands.addCommand(CommandIDs.filebrowserDownload, {
                execute: async () => {
                    const widget = tracker.currentWidget;
                    if (!widget) {
                        return;
                    }
                    const selected = toArray(widget.selectedItems());
                    selected.forEach(async (item) => {
                        if (item.type === 'directory') {
                            return;
                        }
                        const content = await formatContent(item.path);
                        downloadContent(content, item.name);
                    });
                },
                icon: downloadIcon.bindprops({ stylesheet: 'menuItem' }),
                label: trans.__('Download'),
            });
        }
    },
};
/**
 * The main application icon.
 */
const liteLogo = {
    id: '@jupyterlite/application-extension:logo',
    // marking as optional to not throw errors in retro
    optional: [ILabShell],
    autoStart: true,
    activate: (app, labShell) => {
        if (!labShell) {
            return;
        }
        const logo = new Widget();
        liteIcon.element({
            container: logo.node,
            elementPosition: 'center',
            margin: '2px 2px 2px 8px',
            height: 'auto',
            width: '16px',
        });
        logo.id = 'jp-MainLogo';
        labShell.add(logo, 'top', { rank: 0 });
    },
};
/**
 * A plugin to trigger a refresh of the commands when the shell layout changes.
 */
const notifyCommands = {
    id: '@jupyterlite/application-extension:notify-commands',
    autoStart: true,
    optional: [ILabShell],
    activate: (app, labShell) => {
        if (labShell) {
            labShell.layoutModified.connect(() => {
                app.commands.notifyCommandChanged();
            });
        }
    },
};
/**
 * A custom opener plugin to pass the path to documents as
 * query string parameters.
 */
const opener = {
    id: '@jupyterlite/application-extension:opener',
    autoStart: true,
    requires: [IRouter, IDocumentManager],
    optional: [ILabShell],
    activate: (app, router, docManager, labShell) => {
        const { commands } = app;
        const command = 'router:tree';
        commands.addCommand(command, {
            execute: (args) => {
                var _a;
                const parsed = args;
                // use request to do the matching
                const { request, search } = parsed;
                const matches = (_a = request.match(URL_PATTERN)) !== null && _a !== void 0 ? _a : [];
                if (!matches) {
                    return;
                }
                const urlParams = new URLSearchParams(search);
                const paths = urlParams.getAll('path');
                if (!paths) {
                    return;
                }
                const files = paths.map((path) => decodeURIComponent(path));
                app.restored.then(() => {
                    const page = PageConfig.getOption('retroPage');
                    const [file] = files;
                    switch (page) {
                        case 'consoles': {
                            commands.execute('console:create', { path: file });
                            return;
                        }
                        case 'notebooks': {
                            docManager.open(file, NOTEBOOK_FACTORY, undefined, {
                                ref: '_noref',
                            });
                            return;
                        }
                        case 'edit': {
                            docManager.open(file, EDITOR_FACTORY, undefined, {
                                ref: '_noref',
                            });
                            return;
                        }
                        default: {
                            // open all files in the lab interface
                            files.forEach((file) => docManager.open(file));
                            const url = new URL(URLExt.join(PageConfig.getBaseUrl(), request));
                            // only remove the path (to keep extra parameters like the RTC room)
                            url.searchParams.delete('path');
                            const { pathname, search } = url;
                            router.navigate(`${pathname}${search}`, { skipRouting: true });
                            if (labShell) {
                                // open the folder where the files are located on startup
                                const showInBrowser = () => {
                                    commands.execute('docmanager:show-in-file-browser');
                                    labShell.currentChanged.disconnect(showInBrowser);
                                };
                                labShell.currentChanged.connect(showInBrowser);
                            }
                            break;
                        }
                    }
                });
            },
        });
        router.register({ command, pattern: URL_PATTERN });
    },
};
/**
 * A custom plugin to share a link to a file.
 *
 * This url can be used to open a particular file in JupyterLab.
 * It also adds the corresponding room if RTC is enabled.
 *
 */
const shareFile = {
    id: '@jupyterlite/application-extension:share-file',
    requires: [IFileBrowserFactory, ITranslator],
    autoStart: true,
    activate: (app, factory, translator) => {
        const trans = translator.load(I18N_BUNDLE);
        const { commands } = app;
        const { tracker } = factory;
        const roomName = getParam('--room', '').trim();
        const collaborative = PageConfig.getOption('collaborative') === 'true' && roomName;
        commands.addCommand(CommandIDs.copyShareableLink, {
            execute: () => {
                const widget = tracker.currentWidget;
                if (!widget) {
                    return;
                }
                const url = new URL(URLExt.join(PageConfig.getBaseUrl(), 'lab'));
                const models = toArray(filter(widget.selectedItems(), (item) => item.type !== 'directory'));
                models.forEach((model) => {
                    url.searchParams.append('path', model.path);
                });
                if (collaborative) {
                    url.searchParams.append('room', roomName);
                }
                Clipboard.copyToSystem(url.href);
            },
            isVisible: () => !!tracker.currentWidget &&
                toArray(tracker.currentWidget.selectedItems()).length >= 1,
            icon: linkIcon.bindprops({ stylesheet: 'menuItem' }),
            label: trans.__('Copy Shareable Link'),
        });
    },
};
const plugins = [
    about,
    docProviderPlugin,
    downloadPlugin,
    liteLogo,
    notifyCommands,
    opener,
    shareFile,
];
export default plugins;
